import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { api } from '../../lib/api';
import { isRoleAllowed, ROUTE_META } from '../../lib/routeConfig';

const COLLAPSED_STORAGE_KEY = 'nivo:sidebarCollapsed';

// AppLayout — the authenticated shell.
// Responsibilities:
//   1. Fetch /api/auth/me on mount. If 401 -> /login.
//   2. Enforce role-based access. If current path is registered but the user
//      is not allowed, redirect to /commandes (safe fallback).
//   3. Render Sidebar + TopBar + <Outlet /> content area.
//   4. Provide `user` to child routes via Outlet context.
export default function AppLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = React.useState(null);
    const [status, setStatus] = React.useState('loading'); // loading | ready | denied
    const [collapsed, setCollapsed] = React.useState(() => {
        try {
            return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
        } catch (_) {
            return false;
        }
    });

    // Load session on mount.
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await api.me();
                if (cancelled) return;
                setUser(me);
                setStatus('ready');
            } catch (_) {
                if (cancelled) return;
                setStatus('denied');
                navigate('/login', { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    // Role gate: once user is known, verify the current path allows their role.
    React.useEffect(() => {
        if (!user) return;
        // Role check now supports both exact matches (NAV_ITEMS) and sub-paths
        // (e.g. /produits/:id inherits from /produits). isRoleAllowed returns
        // false for genuinely unknown paths — in that case a `*` route in
        // App.js already redirects.
        if (!isRoleAllowed(location.pathname, user.role)) {
            navigate('/commandes', { replace: true });
        }
    }, [user, location.pathname, navigate]);

    const toggleCollapsed = React.useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
            } catch (_) {
                // ignore
            }
            return next;
        });
    }, []);

    if (status !== 'ready' || !user) {
        return (
            <main
                data-testid="app-layout-loading"
                className="min-h-screen w-full bg-[#F5F6FA] flex items-center justify-center"
            >
                <div className="text-[#6B7280] text-sm">Chargement…</div>
            </main>
        );
    }

    return (
        <div
            data-testid="app-layout"
            className="min-h-screen w-full bg-[#F5F6FA] flex"
        >
            <Sidebar user={user} collapsed={collapsed} onToggle={toggleCollapsed} />
            <div className="flex-1 min-w-0 flex flex-col">
                <TopBar user={user} />
                {user.role === 'super_admin' ? (
                    <ChannelAlertsBanner />
                ) : null}
                <main
                    data-testid="content-area"
                    className="flex-1 p-6 bg-[#F5F6FA]"
                >
                    <Outlet context={{ user }} />
                </main>
            </div>
        </div>
    );
}


// Phase W2 — Bannière super admin : liste les canaux WhatsApp en alerte
// (déconnexion, QR expiré, logout). Polling toutes les 20 s. Auto-clear
// côté backend quand un event healthy arrive — ici la bannière disparaît
// dès que la prochaine réponse ne liste plus le canal.
function ChannelAlertsBanner() {
    const [items, setItems] = React.useState([]);
    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await (await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp-channels/alerts`,
                    { credentials: 'include' },
                )).json();
                if (!cancelled && Array.isArray(res.items)) setItems(res.items);
            } catch (_) { /* silent */ }
        };
        load();
        const iv = setInterval(load, 20000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);
    if (!items.length) return null;
    return (
        <div
            data-testid="channel-alerts-banner"
            className="px-4 py-2 bg-[#FEF2F2] border-b border-[#FECACA] text-sm text-[#991B1B] flex items-center gap-2"
        >
            <span aria-hidden>⚠</span>
            <span>
                {items.length === 1
                    ? `Canal ${items[0].commercial_name || items[0].channel_id} déconnecté (${items[0].alert_reason || 'statut'})`
                    : `${items.length} canaux déconnectés : ${items.map((c) => c.commercial_name || c.channel_id).join(', ')}`}
            </span>
        </div>
    );
}
